import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api, { workTasksAPI, staffAPI, checklistAPI, clientWorkTasksAPI, lookupAPI, referralAPI } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import { getInboxTypeLabel } from '../constants/inboxTypes';
import {
  formatDisplayDate,
  isLegacySentinelMilestoneDate,
  parseDateOnlyLocalMs,
  diffCalendarDaysFromToday,
} from '../utils/dateFormat';
import { formatTrackingMilestoneDisplayName } from '../utils/milestoneDisplayName';
import InfoIconButton from './InfoIconButton';
import { hasPermission } from '../utils/permissions';
import { serviceNeedInboxPath } from '../utils/serviceNeedPath';
import './WorkTasksDashboard.css';

/** Split inbox details on http(s) URLs so expanded text shows clickable links. */
function inboxDetailsWithUrlLinks(text) {
  if (text == null || String(text).trim() === '') return null;
  const str = String(text);
  // Match [label](url) markdown links OR bare https:// URLs
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s]+)/g;
  const result = [];
  let last = 0;
  let match;
  let i = 0;
  while ((match = pattern.exec(str)) !== null) {
    if (match.index > last) result.push(<span key={i++}>{str.slice(last, match.index)}</span>);
    if (match[1] && match[2]) {
      const openNewTab = /^https?:\/\//.test(match[2]) || /^\//.test(match[2]);
      result.push(
        <a key={i++} href={match[2]}
          {...(openNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="inbox-details-url-link">
          {match[1]}
        </a>
      );
    } else {
      result.push(
        <a key={i++} href={match[3]} target="_blank" rel="noopener noreferrer" className="inbox-details-url-link">
          {match[3]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < str.length) result.push(<span key={i}>{str.slice(last)}</span>);
  return result;
}

/** First real calendar date for Due column when DB has legacy placeholder years (1901/1950). */
function getEffectiveMilestoneDueDate(task) {
  if (!task) return null;
  const { due_date, latest_date, earliest_date } = task;
  if (due_date && !isLegacySentinelMilestoneDate(due_date)) return due_date;
  if (latest_date && !isLegacySentinelMilestoneDate(latest_date)) return latest_date;
  if (earliest_date && !isLegacySentinelMilestoneDate(earliest_date)) return earliest_date;
  return null;
}

/**
 * Date shown in the Due column. For Window Open (SCHOOL/TPM/TPC), the actionable deadline is the
 * end of the window (latest_date); due_date often equals the window start and confused the "X days" line.
 */
function getDueColumnDisplayDate(task) {
  if (!task) return null;
  if (
    WINDOW_MILESTONE_TYPES.has(task.milestone_type)
    && task.latest_date
    && !isLegacySentinelMilestoneDate(task.latest_date)
  ) {
    return task.latest_date;
  }
  return getEffectiveMilestoneDueDate(task);
}

function formatMilestoneWindowBoundary(value) {
  if (value == null || value === '') return '—';
  return isLegacySentinelMilestoneDate(value) ? '—' : formatDisplayDate(value);
}

/** Only these milestones have a true "Window Open" concept on dashboard. */
const WINDOW_MILESTONE_TYPES = new Set(['SCHOOL', 'TPM', 'TPC']);

/** Page size for /api/work-tasks list (must match backend default). */
const TRACKING_PAGE_SIZE = 100;

/**
 * Must match the date shown in the Due column. Do not use latest_date for every row that has a window —
 * IFSP milestones have a long earliest..latest span but the meaningful "due" is still due_date / effective.
 * Only Window Open (SCHOOL/TPM/TPC) uses the window end in getDueColumnDisplayDate.
 */
function getReferenceDateForCountdown(task) {
  return getDueColumnDisplayDate(task);
}

function WorkTasksDashboard() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [tasks, setTasks] = useState([]);
  /** Total rows matching current filters (tier-1 card can exceed this; list is paginated). */
  const [trackingListTotal, setTrackingListTotal] = useState(null);
  const [trackingPageOffset, setTrackingPageOffset] = useState(0);
  const prevTrackingFilterSigRef = useRef('');
  const [counts, setCounts] = useState({
    overdue: 0,
    dueSoon: 0,
    windowOpen: 0,
    confirmedMeetings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(30);
  const [statusFilter, setStatusFilter] = useState('upcoming');
  const [staffFilter, setStaffFilter] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [inboxItems, setInboxItems] = useState([]);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [expandedInboxId, setExpandedInboxId] = useState(null);
  const [notifSort, setNotifSort] = useState({ by: null, dir: 'asc' });
  const [dashboardInfoOpen, setDashboardInfoOpen] = useState(false);
  const [milestonesSort, setMilestonesSort] = useState({ by: 'due', dir: 'asc' });
  /** Dashboard: user picked Confirmed on a row — must enter meeting date before PATCH. */
  const [dashboardConfirmPending, setDashboardConfirmPending] = useState(null);
  const [dashboardConfirmDateDraft, setDashboardConfirmDateDraft] = useState('');
  // Which Tier 2 panel is expanded: milestones (task table), notifications, or checklists
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [checklistCount, setChecklistCount] = useState(null);
  const [checklistList, setChecklistList] = useState([]);
  const [checklistListLoading, setChecklistListLoading] = useState(false);
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [checklistAssignedScFilter, setChecklistAssignedScFilter] = useState('');
  const [checklistTypeFilter, setChecklistTypeFilter] = useState('');
  const [checklistClientSort, setChecklistClientSort] = useState('asc'); // 'asc' | 'desc' — by client last name
  const [workTasksOpenCount, setWorkTasksOpenCount] = useState(null);
  const [workTasksOpenList, setWorkTasksOpenList] = useState([]);
  const [workTasksOpenListLoading, setWorkTasksOpenListLoading] = useState(false);
  /** My To-Dos open list: one active column sort (same pattern as Client ↑↓). */
  const [workTasksOpenSort, setWorkTasksOpenSort] = useState({ by: 'client', dir: 'asc' });
  const [searchParams, setSearchParams] = useSearchParams();
  const hasHydratedFromUrl = useRef(false);
  /** When URL has no `viewing`, default wide-permission users to self once; cleared if they pick "All staff" so we do not override. */
  const defaultViewingSelfRef = useRef({ applied: false, userChoseAllStaff: false });
  const [trackingAlertStatuses, setTrackingAlertStatuses] = useState([]);
  const [savingTrackingStatusId, setSavingTrackingStatusId] = useState(null);

  const confirmedTrackingStatus = useMemo(
    () => trackingAlertStatuses.find((s) => (s.code || '').toLowerCase() === 'confirmed') || null,
    [trackingAlertStatuses]
  );

  useEffect(() => {
    lookupAPI.getTrackingAlertStatuses()
      .then((res) => setTrackingAlertStatuses(res.data?.data || []))
      .catch(() => setTrackingAlertStatuses([]));
  }, []);

  /** SCs are normally limited to their caseload; work_tasks.view_all enables staff filter (trainers/mentors). */
  const scOwnCaseloadOnly =
    roleCode === 'SERVICE_COORDINATOR' && !hasPermission('work_tasks.view_all');
  const [showHiddenFromCaseload, setShowHiddenFromCaseload] = useState(false);
  const isCoverageDashboardRow = (row, idField = 'assigned_staff_id') => {
    const refId = staffFilter || (scOwnCaseloadOnly ? myStaffId : null);
    if (!refId) return false;
    return row[idField] != null && String(row[idField]) !== String(refId);
  };

  // Restore viewing/panel from URL when returning (e.g. Back from client)
  useEffect(() => {
    if (hasHydratedFromUrl.current) return;
    hasHydratedFromUrl.current = true;
    const viewing = searchParams.get('viewing');
    const panel = searchParams.get('panel');
    if (viewing) {
      setStaffFilter(viewing);
      defaultViewingSelfRef.current.applied = true;
    }
    if (panel === 'milestones' || panel === 'notifications' || panel === 'checklists' || panel === 'worktasks') setExpandedPanel(panel);
  }, []);

  // Managers / trainers (work_tasks.view_all): default "Viewing" to self instead of all staff; URL without `viewing` only.
  useEffect(() => {
    if (scOwnCaseloadOnly || !myStaffId) return;
    if (defaultViewingSelfRef.current.userChoseAllStaff) return;
    if (defaultViewingSelfRef.current.applied) return;
    if (searchParams.get('viewing')) return;
    defaultViewingSelfRef.current.applied = true;
    setStaffFilter(myStaffId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('viewing', myStaffId);
      return next;
    }, { replace: true });
  }, [scOwnCaseloadOnly, myStaffId, searchParams, setSearchParams]);

  const fetchWhoami = React.useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const code = res.data?.membership?.role_code || null;
      const sid = res.data?.user?.staff_id ?? null;
      const uid = res.data?.user?.id ?? null;
      setRoleCode(code);
      setMyStaffId(sid != null ? String(sid) : null);
      setMyUserId(uid != null ? String(uid) : null);
    } catch {
      setRoleCode(null);
      setMyStaffId(null);
      setMyUserId(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  // When "View as" changes, refetch whoami so roleCode/myStaffId match the simulated user (enables Complete etc.)
  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  // Load staff list when user may filter by SC (managers, admins, SCs with work_tasks.view_all)
  useEffect(() => {
    if (scOwnCaseloadOnly) return;
    (async () => {
      try {
        const res = await staffAPI.getAll({ active: 'true' });
        setStaffList(res.data?.data ?? []);
      } catch {
        setStaffList([]);
      }
    })();
  }, [scOwnCaseloadOnly]);

  const trackingFilterSig = `${threshold}|${statusFilter}|${staffFilter}|${scOwnCaseloadOnly}|${showHiddenFromCaseload}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {
          threshold,
          staffId: staffFilter || undefined,
          ...(showHiddenFromCaseload ? { include_hidden_from_caseload: true } : {}),
        };
        const response = await workTasksAPI.getCounts(params);
        if (cancelled) return;
        setCounts((prev) => response.data?.data ?? prev);
      } catch (error) {
        if (!cancelled) console.error('Error loading task counts:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trackingFilterSig]);

  useEffect(() => {
    const sigChanged = prevTrackingFilterSigRef.current !== trackingFilterSig;
    if (sigChanged) {
      prevTrackingFilterSigRef.current = trackingFilterSig;
      if (trackingPageOffset !== 0) {
        setTrackingPageOffset(0);
        return;
      }
    }

    let cancelled = false;
    (async () => {
      try {
        if (trackingPageOffset === 0) setLoading(true);
        const statusForApi = statusFilter === 'upcoming' ? 'upcoming' : statusFilter;
        const params = {
          threshold,
          status: statusForApi === 'all' ? undefined : statusForApi,
          staffId: staffFilter || undefined,
          limit: TRACKING_PAGE_SIZE,
          offset: trackingPageOffset,
          ...(showHiddenFromCaseload ? { include_hidden_from_caseload: true } : {}),
        };
        const response = await workTasksAPI.getTasks(params);
        if (cancelled) return;
        const d = response.data?.data || {};
        setTasks(d.tasks || []);
        setTrackingListTotal(typeof d.totalCount === 'number' ? d.totalCount : null);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading work tasks:', error);
          alert('Failed to load work tasks');
        }
      } finally {
        if (!cancelled && trackingPageOffset === 0) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trackingFilterSig, trackingPageOffset]);

  const [viewingAllStaffNotifications, setViewingAllStaffNotifications] = useState(false);

  const loadInboxItems = async () => {
    try {
      const params = { includeCompleted };
      if (!scOwnCaseloadOnly && staffFilter) params.staffId = staffFilter;
      const res = await workTasksAPI.getInboxItems(params);
      const data = res.data?.data ?? {};
      setInboxItems(data.inboxItems ?? []);
      setViewingAllStaffNotifications(!!data.viewingAllStaff);
    } catch {
      setInboxItems([]);
      setViewingAllStaffNotifications(false);
    }
  };

  useEffect(() => {
    loadInboxItems();
  }, [staffFilter, scOwnCaseloadOnly, includeCompleted]);

  const loadChecklistCount = async () => {
    try {
      const params = {};
      if (!scOwnCaseloadOnly && staffFilter) params.staffId = staffFilter;
      if (!scOwnCaseloadOnly && !staffFilter) params.allStaff = true;
      const res = await checklistAPI.getCount(params);
      setChecklistCount(res.data?.count ?? 0);
    } catch {
      setChecklistCount(0);
    }
  };

  useEffect(() => {
    loadChecklistCount();
  }, [staffFilter, scOwnCaseloadOnly]);

  const loadWorkTasksOpenCount = async () => {
    try {
      const params = {
        ...(showHiddenFromCaseload ? { include_hidden_from_caseload: true } : {}),
      };
      if (!scOwnCaseloadOnly && staffFilter) params.staffId = staffFilter;
      if (!scOwnCaseloadOnly && !staffFilter) params.allStaff = true;
      const res = await clientWorkTasksAPI.getOpenCount(params);
      setWorkTasksOpenCount(res.data?.count ?? 0);
    } catch {
      setWorkTasksOpenCount(0);
    }
  };

  useEffect(() => {
    loadWorkTasksOpenCount();
  }, [staffFilter, scOwnCaseloadOnly, showHiddenFromCaseload]);

  const viewingAllStaffChecklists = !scOwnCaseloadOnly && !staffFilter;

  const loadChecklistList = async () => {
    try {
      setChecklistListLoading(true);
      const params = { openOnly: true };
      if (!scOwnCaseloadOnly && staffFilter) params.staffId = staffFilter;
      if (viewingAllStaffChecklists) params.allStaff = true;
      const res = await checklistAPI.getMine(params);
      setChecklistList(res.data?.data ?? []);
    } catch {
      setChecklistList([]);
    } finally {
      setChecklistListLoading(false);
    }
  };

  useEffect(() => {
    if (expandedPanel === 'checklists') loadChecklistList();
  }, [expandedPanel, staffFilter, scOwnCaseloadOnly]);

  const loadWorkTasksOpenList = async () => {
    try {
      setWorkTasksOpenListLoading(true);
      const params = {
        ...(showHiddenFromCaseload ? { include_hidden_from_caseload: true } : {}),
      };
      if (!scOwnCaseloadOnly && staffFilter) params.staffId = staffFilter;
      if (viewingAllStaffChecklists) params.allStaff = true;
      const res = await clientWorkTasksAPI.getOpenList(params);
      setWorkTasksOpenList(res.data?.data ?? []);
    } catch {
      setWorkTasksOpenList([]);
    } finally {
      setWorkTasksOpenListLoading(false);
    }
  };

  useEffect(() => {
    if (expandedPanel === 'worktasks') loadWorkTasksOpenList();
  }, [expandedPanel, staffFilter, roleCode, scOwnCaseloadOnly, showHiddenFromCaseload]);

  const toggleWorkTasksOpenSort = React.useCallback((by) => {
    setWorkTasksOpenSort((prev) =>
      prev.by === by
        ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { by, dir: 'asc' }
    );
  }, []);

  const displayedWorkTasksOpenList = React.useMemo(() => {
    const itemLabelForSort = (row) => (row.kind === 'general' ? 'General' : 'Work task');
    const effectiveTodoDate = (row) => {
      const isGeneral = row.kind === 'general';
      const raw = isGeneral ? row.due_date : row.requested_date;
      if (raw == null || String(raw).trim() === '') return null;
      return String(raw).trim();
    };
    const cmpDate = (a, b) => {
      const da = effectiveTodoDate(a);
      const db = effectiveTodoDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    };
    const cmpClient = (a, b) => {
      const aLast = (a.client_last_name || '').toLowerCase();
      const bLast = (b.client_last_name || '').toLowerCase();
      return aLast.localeCompare(bLast);
    };
    const cmpItem = (a, b) => itemLabelForSort(a).localeCompare(itemLabelForSort(b));
    const tieBreak = (a, b) => {
      const c = cmpClient(a, b);
      if (c !== 0) return c;
      return String(a.id || '').localeCompare(String(b.id || ''));
    };

    const { by, dir } = workTasksOpenSort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...workTasksOpenList].sort((a, b) => {
      let cmp = 0;
      if (by === 'client') cmp = cmpClient(a, b);
      else if (by === 'item') cmp = cmpItem(a, b);
      else cmp = cmpDate(a, b);
      if (cmp !== 0) return cmp * mult;
      return tieBreak(a, b);
    });
  }, [workTasksOpenList, workTasksOpenSort]);

  useEffect(() => {
    if (expandedPanel !== 'checklists') return;
    checklistAPI.getTypes()
      .then((res) => setChecklistTypes(res.data?.data ?? []))
      .catch(() => setChecklistTypes([]));
  }, [expandedPanel]);

  // Client-side filter and sort for checklist table (Assigned SC + Type; sort by client last name)
  const displayedChecklistList = React.useMemo(() => {
    let list = checklistList;
    if (viewingAllStaffChecklists && checklistAssignedScFilter) {
      list = list.filter((row) => String(row.assignee_id || '') === String(checklistAssignedScFilter));
    }
    if (checklistTypeFilter) {
      list = list.filter((row) => String(row.checklist_type_id || '') === String(checklistTypeFilter));
    }
    const byLastName = (a, b) => {
      const aLast = (a.client_last_name || '').toLowerCase();
      const bLast = (b.client_last_name || '').toLowerCase();
      const cmp = aLast.localeCompare(bLast);
      return checklistClientSort === 'asc' ? cmp : -cmp;
    };
    return [...list].sort(byLastName);
  }, [checklistList, viewingAllStaffChecklists, checklistAssignedScFilter, checklistTypeFilter, checklistClientSort]);

  const formatDate = (dateString) => (dateString ? formatDisplayDate(dateString) : 'N/A');

  const getDueWithinNextLabel = () => `Due within the next ${threshold} days`;

  const getStatusLabel = (taskStatus) => {
    switch (taskStatus) {
      case 'overdue':
        return 'Overdue';
      case 'due_soon':
        return getDueWithinNextLabel();
      case 'upcoming':
        return getDueWithinNextLabel();
      case 'confirmed_meetings':
        return `Confirmed meetings (through next ${threshold} days; includes past until completed)`;
      case 'window_open':
        return 'Window Open';
      case 'too_early':
        return 'Too Early';
      case 'actionable':
        return 'Actionable';
      case 'all':
        return 'All';
      default:
        return 'Pending';
    }
  };

  const getStatusSortOrder = (taskStatus) => {
    const order = { overdue: 0, due_soon: 1, window_open: 2, too_early: 3 };
    return order[taskStatus] ?? 4;
  };

  const sortedTasks = React.useMemo(() => {
    if (expandedPanel !== 'milestones' || !tasks.length) return tasks;
    const dir = milestonesSort.dir === 'asc' ? 1 : -1;
    return [...tasks].sort((a, b) => {
      let cmp = 0;
      switch (milestonesSort.by) {
        case 'client': {
          const nameA = `${(a.last_name || '')} ${(a.first_name || '')}`.toLowerCase();
          const nameB = `${(b.last_name || '')} ${(b.first_name || '')}`.toLowerCase();
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case 'status':
          cmp = getStatusSortOrder(a.task_status) - getStatusSortOrder(b.task_status);
          break;
        case 'milestone':
          cmp = formatTrackingMilestoneDisplayName(a).localeCompare(formatTrackingMilestoneDisplayName(b));
          break;
        case 'due': {
          const dateA = parseDateOnlyLocalMs(getDueColumnDisplayDate(a));
          const dateB = parseDateOnlyLocalMs(getDueColumnDisplayDate(b));
          cmp = dateA - dateB;
          break;
        }
        case 'meeting': {
          const dateA = parseDateOnlyLocalMs(a.confirmed_meeting_date);
          const dateB = parseDateOnlyLocalMs(b.confirmed_meeting_date);
          cmp = dateA - dateB;
          break;
        }
        default:
          return 0;
      }
      return dir * cmp;
    });
  }, [tasks, expandedPanel, milestonesSort.by, milestonesSort.dir]);

  const handleMilestonesSort = (column) => {
    setMilestonesSort((prev) => {
      if (prev.by === column) {
        return { by: column, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { by: column, dir: 'asc' };
    });
  };

  const SortableTh = ({ column, label }) => {
    const isActive = milestonesSort.by === column;
    const arrow = isActive ? (milestonesSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    const labelText = typeof label === 'string' ? label.replace(/\n/g, ' ') : '';
    const twoLine = typeof label === 'string' && label.includes('\n');
    return (
      <th className="tasks-table-th-sortable">
        <button
          type="button"
          className="tasks-table-sort-btn"
          onClick={() => handleMilestonesSort(column)}
          aria-sort={isActive ? (milestonesSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
          aria-label={`Sort by ${labelText}${isActive ? ` (${milestonesSort.dir === 'asc' ? 'ascending' : 'descending'}), click to toggle` : ', click to sort'}`}
        >
          <span className={`tasks-table-sort-label ${twoLine ? 'tasks-table-sort-label-two-line' : ''}`}>{label}{arrow}</span>
        </button>
      </th>
    );
  };

  const handleNotifSort = (col) => {
    setNotifSort((prev) => {
      if (prev.by === col) return { by: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { by: col, dir: 'asc' };
    });
  };

  const sortedInboxItems = React.useMemo(() => {
    if (!notifSort.by) return inboxItems;
    const dir = notifSort.dir === 'asc' ? 1 : -1;
    return [...inboxItems].sort((a, b) => {
      if (notifSort.by === 'type') {
        return dir * getInboxTypeLabel(a.type).localeCompare(getInboxTypeLabel(b.type));
      }
      if (notifSort.by === 'assignee') {
        const nameA = [a.assignee_last_name, a.assignee_first_name].filter(Boolean).join(' ') || '';
        const nameB = [b.assignee_last_name, b.assignee_first_name].filter(Boolean).join(' ') || '';
        return dir * nameA.localeCompare(nameB);
      }
      return 0;
    });
  }, [inboxItems, notifSort]);

  const handleTrackingStatusChange = async (task, value) => {
    const milestoneId = task.milestone_id;
    const referralId = task.referral_id;
    const statusId = value || null;
    if (dashboardConfirmPending && dashboardConfirmPending.milestoneId !== milestoneId) {
      setDashboardConfirmPending(null);
      setDashboardConfirmDateDraft('');
    }
    const pickingConfirmed =
      confirmedTrackingStatus &&
      statusId &&
      String(statusId) === String(confirmedTrackingStatus.id);
    if (pickingConfirmed) {
      setDashboardConfirmPending({ referralId, milestoneId, statusId });
      setDashboardConfirmDateDraft('');
      return;
    }
    if (dashboardConfirmPending?.milestoneId === milestoneId) {
      setDashboardConfirmPending(null);
      setDashboardConfirmDateDraft('');
    }
    try {
      setSavingTrackingStatusId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(referralId, milestoneId, {
        tracking_alert_status_id: statusId,
      });
      const newName = statusId ? (trackingAlertStatuses.find((s) => String(s.id) === String(statusId))?.name ?? null) : null;
      setTasks((prev) => prev.map((t) => (t.milestone_id === milestoneId ? { ...t, tracking_alert_status_id: statusId, tracking_alert_status_name: newName } : t)));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update tracking status');
    } finally {
      setSavingTrackingStatusId(null);
    }
  };

  const applyDashboardConfirmedMeeting = async () => {
    if (!dashboardConfirmPending) return;
    const dateStr = (dashboardConfirmDateDraft || '').trim();
    if (!dateStr) {
      alert('Confirmed meeting date is required when status is Confirmed.');
      return;
    }
    const { referralId, milestoneId, statusId } = dashboardConfirmPending;
    try {
      setSavingTrackingStatusId(milestoneId);
      await referralAPI.updateMilestoneTrackingStatus(referralId, milestoneId, {
        tracking_alert_status_id: statusId,
        confirmed_meeting_date: dateStr,
      });
      setDashboardConfirmPending(null);
      setDashboardConfirmDateDraft('');
      const newName = trackingAlertStatuses.find((s) => String(s.id) === String(statusId))?.name ?? 'Confirmed';
      setTasks((prev) =>
        prev.map((t) =>
          t.milestone_id === milestoneId
            ? {
                ...t,
                tracking_alert_status_id: statusId,
                tracking_alert_status_name: newName,
                confirmed_meeting_date: dateStr,
              }
            : t
        )
      );
      try {
        const countRes = await workTasksAPI.getCounts({
          threshold,
          staffId: staffFilter || undefined,
        });
        setCounts((prev) => countRes.data?.data ?? prev);
      } catch {
        /* ignore */
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save confirmed meeting');
    } finally {
      setSavingTrackingStatusId(null);
    }
  };

  const handleInboxItemStatus = async (itemId, status) => {
    try {
      await workTasksAPI.updateInboxItemStatus(itemId, status);
      if (includeCompleted) {
        setInboxItems((prev) => prev.map((a) => (a.id === itemId ? { ...a, status } : a)));
      } else {
        setInboxItems((prev) => prev.filter((a) => a.id !== itemId));
      }
      setExpandedInboxId((id) => (id === itemId ? null : id));
    } catch (err) {
      console.error('Failed to update inbox item:', err);
      alert('Failed to update inbox item');
    }
  };

  const getDaysUntilDue = (dueDate) => diffCalendarDaysFromToday(dueDate);

  if (loading) {
    return <div className="loading">Loading work tasks...</div>;
  }

  return (
    <div className="work-tasks-dashboard">
      <div className="header">
        <div className="work-tasks-dashboard-title-row">
          <h1 className="page-header-title">Dashboard</h1>
          <InfoIconButton
            onClick={() => setDashboardInfoOpen((v) => !v)}
            ariaExpanded={dashboardInfoOpen}
            ariaControls="work-tasks-dashboard-info"
            ariaLabel="Show explanation"
            ariaLabelExpanded="Hide explanation"
            className="work-tasks-dashboard-info-icon"
          />
        </div>
        {dashboardInfoOpen && (
          <p id="work-tasks-dashboard-info" className="work-tasks-dashboard-info-text">What needs your attention next?</p>
        )}
        {scOwnCaseloadOnly && (
          <div className="work-tasks-dashboard-show-hidden-caseload">
            <label>
              <input
                type="checkbox"
                checked={showHiddenFromCaseload}
                onChange={(e) => setShowHiddenFromCaseload(e.target.checked)}
              />
              Show hidden
            </label>
          </div>
        )}
        {!scOwnCaseloadOnly && (
          <div className="header-viewing">
            <label htmlFor="dashboard-staff-filter">Viewing:</label>
            <select
              id="dashboard-staff-filter"
              value={staffFilter}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  defaultViewingSelfRef.current.userChoseAllStaff = true;
                }
                setStaffFilter(v);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (v) next.set('viewing', v); else next.delete('viewing');
                  return next;
                }, { replace: true });
              }}
              className="header-viewing-select"
              title="Filter by assigned staff (managers/admins)"
            >
              <option value="">All staff</option>
              {staffList.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.last_name}, {s.first_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tier 1: Time-based buckets — click to filter milestones and show list */}
      <div className="summary-cards tier1-cards">
        <button
          type="button"
          className={`summary-card card-overdue ${statusFilter === 'overdue' ? 'card-selected' : ''}`}
          onClick={() => {
            setStatusFilter('overdue');
            setExpandedPanel('milestones');
            setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('panel', 'milestones'); return p; }, { replace: true });
          }}
        >
          <div className="card-value">{counts.overdue}</div>
          <div className="card-label">Overdue</div>
        </button>
        <button
          type="button"
          className={`summary-card card-due-soon ${statusFilter === 'upcoming' ? 'card-selected' : ''}`}
          onClick={() => {
            setStatusFilter('upcoming');
            setExpandedPanel('milestones');
            setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('panel', 'milestones'); return p; }, { replace: true });
          }}
        >
          <div className="card-value">{(counts.dueSoon || 0) + (counts.windowOpen || 0)}</div>
          <div className="card-label card-label-due-within" aria-live="polite">
            <span className="card-label-due-within-line">Due within the next</span>
            <span className="card-label-due-within-days">{threshold} days</span>
          </div>
          <div className="card-label-subline">
            School, TPM, TPC: open window or window overlaps next {threshold} days
          </div>
        </button>
        <button
          type="button"
          className={`summary-card card-confirmed-meetings ${statusFilter === 'confirmed_meetings' ? 'card-selected' : ''}`}
          onClick={() => {
            setStatusFilter('confirmed_meetings');
            setMilestonesSort({ by: 'meeting', dir: 'asc' });
            setExpandedPanel('milestones');
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              p.set('panel', 'milestones');
              return p;
            }, { replace: true });
          }}
        >
          <div className="card-value">{counts.confirmedMeetings ?? 0}</div>
          <div className="card-label card-label-due-within" aria-live="polite">
            <span className="card-label-due-within-line">Confirmed meetings</span>
            <span className="card-label-due-within-days">through next {threshold} days</span>
          </div>
          <div className="card-label-subline">
            Not also counted under Due within the next {threshold} days (same milestone is not listed twice)
          </div>
        </button>
      </div>

      {/* Tier 2: Source buckets — click to expand that panel */}
      <div className="tier2-cards">
        <div
          className={`tier2-card tier2-card-milestones ${expandedPanel === 'milestones' ? 'tier2-card-selected' : ''}`}
        >
          <button
            type="button"
            className="tier2-card-main"
            onClick={() => {
              const next = expandedPanel === 'milestones' ? null : 'milestones';
              setExpandedPanel(next);
              setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                if (next) p.set('panel', next); else p.delete('panel');
                return p;
              }, { replace: true });
            }}
          >
            <div className="tier2-card-value">
              {trackingListTotal != null ? trackingListTotal : (tasks.length || '—')}
            </div>
            <div className="tier2-card-label">Tracking</div>
            <div className="tier2-card-hint">Due dates from referrals · {TRACKING_PAGE_SIZE} rows per page</div>
          </button>
          <Link
            to={tenantPath('/dashboard/rules')}
            className="tier2-card-link"
            onClick={(e) => e.stopPropagation()}
            title="How due and window dates are calculated"
          >
            Rules
          </Link>
        </div>
        <button
          type="button"
          className={`tier2-card ${expandedPanel === 'notifications' ? 'tier2-card-selected' : ''}`}
          onClick={() => {
            const next = expandedPanel === 'notifications' ? null : 'notifications';
            setExpandedPanel(next);
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              if (next) p.set('panel', next); else p.delete('panel');
              return p;
            }, { replace: true });
          }}
        >
          <div className="tier2-card-value">{inboxItems.filter((i) => i.status !== 'completed').length}</div>
          <div className="tier2-card-label">Notifications</div>
          <div className="tier2-card-hint">Updates and items for you</div>
        </button>
        <button
          type="button"
          className={`tier2-card ${expandedPanel === 'checklists' ? 'tier2-card-selected' : ''}`}
          onClick={() => {
            const next = expandedPanel === 'checklists' ? null : 'checklists';
            setExpandedPanel(next);
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              if (next) p.set('panel', next); else p.delete('panel');
              return p;
            }, { replace: true });
          }}
        >
          <div className="tier2-card-value">{checklistCount !== null ? checklistCount : '—'}</div>
          <div className="tier2-card-label">Checklists</div>
        </button>
        <button
          type="button"
          className={`tier2-card ${expandedPanel === 'worktasks' ? 'tier2-card-selected' : ''}`}
          onClick={() => {
            const next = expandedPanel === 'worktasks' ? null : 'worktasks';
            setExpandedPanel(next);
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              if (next) p.set('panel', next); else p.delete('panel');
              return p;
            }, { replace: true });
          }}
        >
          <div className="tier2-card-value">{workTasksOpenCount !== null ? workTasksOpenCount : '—'}</div>
          <div className="tier2-card-label">My To-Dos</div>
          <div className="tier2-card-hint">Open work tasks &amp; your general to-dos</div>
        </button>
      </div>

      {/* Detail area: one panel at a time */}
      <div className="dashboard-detail">
        {expandedPanel === 'milestones' && (
          <>
            <div className="milestones-panel-toolbar">
              <span className="milestones-panel-showing">
                Showing: <strong>{getStatusLabel(statusFilter)}</strong>
                {trackingListTotal != null && tasks.length > 0 && (
                  <span className="milestones-panel-row-range">
                    {' '}
                    — rows {trackingPageOffset + 1}–{trackingPageOffset + tasks.length} of {trackingListTotal}
                  </span>
                )}
                {trackingListTotal != null && tasks.length === 0 && (
                  <span className="milestones-panel-row-range"> — no rows on this page</span>
                )}
              </span>
              <div className="milestones-panel-pagination" role="navigation" aria-label="Tracking list pages">
                <button
                  type="button"
                  className="milestones-panel-page-btn"
                  disabled={trackingPageOffset <= 0}
                  onClick={() => setTrackingPageOffset((o) => Math.max(0, o - TRACKING_PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="milestones-panel-page-btn"
                  disabled={
                    trackingListTotal == null
                    || trackingPageOffset + tasks.length >= trackingListTotal
                  }
                  onClick={() => setTrackingPageOffset((o) => o + TRACKING_PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
              <label htmlFor="milestones-days-ahead" className="milestones-panel-toolbar-label">Show next:</label>
              <select
                id="milestones-days-ahead"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="milestones-panel-select"
                title={
                  statusFilter === 'confirmed_meetings'
                    ? `List confirmed meetings with a date on or before today + ${threshold} days (past dates remain until the milestone is completed).`
                    : 'How far ahead to include upcoming tracking items; School/TPM/TPC when the legal window overlaps today through this many days ahead, plus other milestones due in that range.'
                }
              >
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
                <option value="60">60 days</option>
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">365 days</option>
              </select>
            </div>
      <div className="tasks-table">
        {statusFilter === 'confirmed_meetings' ? (
          <table className="tasks-table-confirmed-meetings">
            <thead>
              <tr>
                <SortableTh column="client" label="Client" />
                <SortableTh column="milestone" label="Tracking item" />
                <SortableTh column="meeting" label={'Confirmed\nmeeting date'} />
              </tr>
            </thead>
            <tbody>
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={3} className="no-data">
                    No confirmed meetings in this view
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task) => {
                  const milestoneLabel = formatTrackingMilestoneDisplayName(task);
                  const milestonesUrl = `${tenantPath(`/client/${task.client_id}/milestones`)}?referralId=${encodeURIComponent(task.referral_id)}`;
                  return (
                    <tr
                      key={task.milestone_id}
                      className="task-row"
                      onClick={() => navigate(milestonesUrl)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(milestonesUrl);
                        }
                      }}
                    >
                      <td className="tasks-cell-client">
                        <Link to={milestonesUrl} className="tasks-client-link" onClick={(e) => e.stopPropagation()}>
                          {task.last_name}, {task.first_name}
                        </Link>
                      </td>
                      <td>
                        <span className="milestone-name">{milestoneLabel}</span>
                      </td>
                      <td className="tasks-cell-confirmed-meeting">
                        {task.confirmed_meeting_date ? formatDate(task.confirmed_meeting_date) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <SortableTh column="client" label="Client" />
                <SortableTh column="status" label={'Dashboard\nStatus'} />
                <th className="tasks-col-tracking tasks-th-two-line">Tracking<br />Status</th>
                <SortableTh column="milestone" label="Tracking item" />
                <SortableTh column="due" label="Due" />
                <th className="tasks-col-window">Window</th>
                <th className="tasks-col-assigned tasks-th-two-line">Assigned<br />Staff</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-data">
                    No tasks found matching your filters
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task) => {
                  const hasWindow =
                    WINDOW_MILESTONE_TYPES.has(task.milestone_type) &&
                    task.earliest_date &&
                    task.latest_date;
                  const referenceDate = getReferenceDateForCountdown(task);
                  const daysUntilDue = getDaysUntilDue(referenceDate);
                  const dueDisplayDate = getDueColumnDisplayDate(task);
                  const milestoneLabel = formatTrackingMilestoneDisplayName(task);
                  const isExit = task.milestone_type === 'EXIT';
                  // Exit overdue is normal (child ages out) — show neutral, not red.
                  const dueRelativeClass = daysUntilDue < 0 && !isExit ? 'days-overdue' : 'days-remaining';
                  // For Exit rows, also show the child's age-out date (third birthday minus one day).
                  let ageOutDate = null;
                  if (isExit && task.date_of_birth) {
                    const dob = new Date(`${String(task.date_of_birth).slice(0, 10)}T00:00:00`);
                    if (!Number.isNaN(dob.getTime())) {
                      ageOutDate = formatDate(new Date(dob.getFullYear() + 3, dob.getMonth(), dob.getDate() - 1));
                    }
                  }
                  return (
                    <tr
                      key={task.milestone_id}
                      className={`task-row task-${task.task_status}`}
                      onClick={() => navigate(tenantPath(`/client/${task.client_id}`))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(tenantPath(`/client/${task.client_id}`)); } }}
                    >
                      <td className="tasks-cell-client">
                        <Link
                          to={tenantPath(`/client/${task.client_id}`)}
                          className="tasks-client-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {task.last_name}, {task.first_name}
                        </Link>
                        {isCoverageDashboardRow(task) && (
                          <span className="dashboard-coverage-badge" title="You are covering for this client's primary SC">Cov</span>
                        )}
                      </td>
                      <td className="tasks-cell-status">{getStatusLabel(task.task_status)}</td>
                      <td className="tasks-cell-tracking" onClick={(e) => e.stopPropagation()}>
                        <div className="tasks-tracking-status-stack">
                          <select
                            aria-label={`Tracking status for ${milestoneLabel}`}
                            className="tasks-tracking-status-select"
                            value={
                              dashboardConfirmPending?.milestoneId === task.milestone_id
                                ? dashboardConfirmPending.statusId
                                : (task.tracking_alert_status_id || '')
                            }
                            onChange={(e) => handleTrackingStatusChange(task, e.target.value || null)}
                            disabled={!!savingTrackingStatusId}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">—</option>
                            {trackingAlertStatuses.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                          {dashboardConfirmPending?.milestoneId === task.milestone_id && (
                            <div className="tasks-dashboard-confirm-inline">
                              <label htmlFor={`dash-confirm-date-${task.milestone_id}`} className="tasks-dashboard-confirm-label">
                                Meeting date
                              </label>
                              <input
                                id={`dash-confirm-date-${task.milestone_id}`}
                                type="date"
                                className="tasks-dashboard-confirm-date"
                                value={dashboardConfirmDateDraft}
                                onChange={(e) => setDashboardConfirmDateDraft(e.target.value)}
                              />
                              <button
                                type="button"
                                className="tasks-dashboard-confirm-save"
                                onClick={applyDashboardConfirmedMeeting}
                                disabled={!!savingTrackingStatusId}
                              >
                                Save
                              </button>
                            </div>
                          )}
                          {savingTrackingStatusId === task.milestone_id && (
                            <span className="tasks-tracking-status-saving" aria-hidden="true">Saving…</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="milestone-name">{milestoneLabel}</span>
                      </td>
                      <td className="tasks-cell-due">
                        <div className="due-date">{dueDisplayDate ? formatDate(dueDisplayDate) : '—'}</div>
                        {daysUntilDue !== null && (
                          <div className={dueRelativeClass}>
                            {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days ago` : `${daysUntilDue} days`}
                          </div>
                        )}
                        {ageOutDate && (
                          <div className="days-remaining">Age Out: {ageOutDate}</div>
                        )}
                      </td>
                      <td className="tasks-cell-window">
                        {hasWindow ? (
                          <span className="window-stacked">
                            <span className="window-from">{formatMilestoneWindowBoundary(task.earliest_date)}</span>
                            <span className="window-to">{formatMilestoneWindowBoundary(task.latest_date)}</span>
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td className="tasks-cell-assigned">
                        {task.staff_first_name && task.staff_last_name ? (
                          `${task.staff_first_name} ${task.staff_last_name}`
                        ) : (
                          'Unassigned'
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
          </>
        )}

        {expandedPanel === 'notifications' && (
          <section className="assignments-section notifications-section">
            <div className="inbox-section-header">
              <h2 className="assignments-heading">
                {viewingAllStaffNotifications
                  ? 'Notifications (all staff)'
                  : !scOwnCaseloadOnly && staffFilter && staffFilter !== myStaffId
                    ? (() => {
                        const s = staffList.find((x) => String(x.id) === staffFilter);
                        return s ? `Notifications for ${s.first_name} ${s.last_name}` : 'Notifications';
                      })()
                    : 'Notifications'}
              </h2>
              <label className="inbox-include-completed">
                <input
                  type="checkbox"
                  checked={includeCompleted}
                  onChange={(e) => setIncludeCompleted(e.target.checked)}
                />
                Include completed
              </label>
            </div>
            <div className="inbox-box">
              <table className="inbox-table">
                <thead>
                  <tr>
                    <th className="inbox-col-expand" aria-label="Expand" />
                    {viewingAllStaffNotifications && (
                      <th className="inbox-col-assignee tasks-table-th-sortable">
                        <button
                          type="button"
                          className="tasks-table-sort-btn"
                          onClick={() => handleNotifSort('assignee')}
                          aria-sort={notifSort.by === 'assignee' ? (notifSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          aria-label={`Sort by Assignee${notifSort.by === 'assignee' ? `, click to toggle` : ''}`}
                        >
                          <span className="tasks-table-sort-label">Assignee{notifSort.by === 'assignee' ? (notifSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                        </button>
                      </th>
                    )}
                    <th className="inbox-col-type tasks-table-th-sortable">
                      <button
                        type="button"
                        className="tasks-table-sort-btn"
                        onClick={() => handleNotifSort('type')}
                        aria-sort={notifSort.by === 'type' ? (notifSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        aria-label={`Sort by Type${notifSort.by === 'type' ? `, click to toggle` : ''}`}
                      >
                        <span className="tasks-table-sort-label">Type{notifSort.by === 'type' ? (notifSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}</span>
                      </button>
                    </th>
                    <th className="inbox-col-status">Status</th>
                    <th className="inbox-col-description">Description</th>
                    <th className="inbox-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInboxItems.map((a) => {
                    const canComplete =
                      (a.status === 'open' || a.status === 'pending') &&
                      (viewingAllStaffNotifications ? String(a.assignee_id) === String(myStaffId) : (!staffFilter || staffFilter === myStaffId));
                    const isExpanded = expandedInboxId === a.id;
                    const hasDetails = a.details && a.details.trim() !== '';
                    return (
                      <React.Fragment key={a.id}>
                        <tr
                          className={`inbox-row ${a.status === 'completed' ? 'inbox-row-completed' : ''} ${a.type === 'client_assignment' ? 'inbox-row-client-assignment' : ''} ${a.type === 'cs_need_delay' ? 'inbox-row-cs-need-delay' : ''} ${a.type === 'cs_need_change' ? 'inbox-row-cs-need-change' : ''} ${isExpanded ? 'inbox-row-expanded' : ''} ${hasDetails ? 'inbox-row-expandable' : ''}`}
                          onClick={() => hasDetails && setExpandedInboxId((id) => (id === a.id ? null : a.id))}
                        >
                          <td className="inbox-col-expand">
                            {hasDetails ? (
                              <span className="inbox-expand-icon" aria-hidden>{isExpanded ? '▼' : '▶'}</span>
                            ) : null}
                          </td>
                          {viewingAllStaffNotifications && (
                            <td className="inbox-col-assignee">
                              {a.assignee_first_name || a.assignee_last_name
                                ? [a.assignee_first_name, a.assignee_last_name].filter(Boolean).join(' ')
                                : '—'}
                            </td>
                          )}
                          <td className="inbox-col-type">
                            <span className={`inbox-type-badge${a.type === 'client_assignment' ? ' inbox-type-badge-client-assignment' : ''}${a.type === 'cs_need_delay' ? ' inbox-type-badge-cs-need-delay' : ''}${a.type === 'cs_need_change' ? ' inbox-type-badge-cs-need-change' : ''}`}>{getInboxTypeLabel(a.type)}</span>
                          </td>
                          <td className="inbox-col-status">
                            <span className={`assignment-status assignment-status-${a.status}`}>{a.status}</span>
                          </td>
                          <td className="inbox-col-description">
                            <span className="inbox-description-text">{a.description}</span>
                            {(a.assigner_first_name || a.due_at || a.created_at) && (
                              <span className="inbox-meta">
                                {a.assigner_first_name && `From ${a.assigner_first_name} ${a.assigner_last_name}`}
                                {a.assigner_first_name && a.due_at && ' · '}
                                {a.due_at && `Due ${formatDate(a.due_at)}`}
                                {(a.assigner_first_name || a.due_at) && a.created_at && ' · '}
                                {a.created_at && `Sent ${formatDate(a.created_at)}`}
                                {a.updated_at && a.created_at && (new Date(a.updated_at) - new Date(a.created_at)) > 60000 && (
                                  <span className="inbox-meta-updated"> · Updated {formatDate(a.updated_at)}</span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="inbox-col-actions" onClick={(e) => e.stopPropagation()}>
                            <span className="inbox-action-buttons">
                              {a.cs_need_id &&
                                (hasPermission('client_services.view_all') ||
                                  hasPermission('client_services.manage_needs') ||
                                  hasPermission('client_services.search_providers')) && (
                                <Link
                                  to={serviceNeedInboxPath(tenantPath, {
                                    csNeedId: a.cs_need_id,
                                    clientId: a.client_id,
                                    referralId: a.referral_id,
                                  })}
                                  className="notifications-view-link"
                                >
                                  View service need
                                </Link>
                              )}
                              {a.type === 'eies_sped' && a.client_id && a.referral_id && (
                                <Link
                                  to={`${tenantPath(`/client/${a.client_id}/milestones`)}?referralId=${encodeURIComponent(a.referral_id)}`}
                                  className="notifications-view-link"
                                >
                                  View client tracking
                                </Link>
                              )}
                              {a.client_id && (
                                <Link to={tenantPath(`/client/${a.client_id}`)} className="notifications-view-link">View client</Link>
                              )}
                              {a.at_packet_id && (
                                <Link to={tenantPath(`/lists/at-packets/${a.at_packet_id}`)} className="notifications-view-link">View packet</Link>
                              )}
                              {a.fr_review_id && (
                                <Link to={tenantPath(`/lists/file-reviews/${a.fr_review_id}`)} className="notifications-view-link">View review</Link>
                              )}
                              {a.referral_id && (
                                <Link to={tenantPath(`/referral/${a.referral_id}`)} className="notifications-view-link">View referral</Link>
                              )}
                              {a.transfer_id && (
                                <Link to={tenantPath(`/lists/transfers/${a.transfer_id}`)} className="notifications-view-link">View transfer</Link>
                              )}
                              {a.support_case_id && (
                                <Link to={tenantPath(`/lists/support-cases/${a.support_case_id}`)} className="notifications-view-link">View case</Link>
                              )}
                              {canComplete && (
                                <button type="button" className="btn-complete-assignment" onClick={() => handleInboxItemStatus(a.id, 'completed')}>Complete</button>
                              )}
                              {a.status === 'completed' && a.updated_at && (
                                <span className="inbox-completed-at" title="Completed">Completed {formatDate(a.updated_at)}</span>
                              )}
                            </span>
                          </td>
                        </tr>
                        {hasDetails && isExpanded && (
                          <tr className="inbox-details-row">
                            <td colSpan={viewingAllStaffNotifications ? 6 : 5} className="inbox-details-cell">
                              <div className="inbox-details-content">{inboxDetailsWithUrlLinks(a.details)}</div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {inboxItems.length === 0 && includeCompleted && (
              <p className="inbox-empty">No notifications (including completed).</p>
            )}
            {inboxItems.length === 0 && !includeCompleted && (
              <p className="inbox-empty">No notifications right now.</p>
            )}
          </section>
        )}

        {expandedPanel === 'checklists' && (
          <section className="assignments-section checklists-section">
            <div className="inbox-section-header">
              <h2 className="assignments-heading">
                {viewingAllStaffChecklists
                  ? 'Checklists (all staff)'
                  : !scOwnCaseloadOnly && staffFilter && staffFilter !== myStaffId
                    ? (() => {
                        const s = staffList.find((x) => String(x.id) === staffFilter);
                        return s ? `Checklists for ${s.first_name} ${s.last_name}` : 'Checklists';
                      })()
                    : 'Checklists'}
              </h2>
            </div>
            <div className="inbox-box">
              <table className="inbox-table checklists-table table-with-filter-row">
                <thead>
                  <tr className="table-header-row">
                    <th
                      className="inbox-col-client checklists-sortable"
                      onClick={() => setChecklistClientSort((s) => (s === 'asc' ? 'desc' : 'asc'))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChecklistClientSort((s) => (s === 'asc' ? 'desc' : 'asc')); } }}
                      title="Sort by client last name"
                    >
                      Client{checklistClientSort === 'asc' ? ' ↑' : ' ↓'}
                    </th>
                    {viewingAllStaffChecklists && <th className="inbox-col-assignee">Assigned SC</th>}
                    <th className="inbox-col-type">Type</th>
                  </tr>
                  <tr className="my-checklists-filter-row">
                    <th className="inbox-col-client">
                      <span className="table-filter-placeholder" aria-hidden="true" />
                    </th>
                    {viewingAllStaffChecklists && (
                      <th className="inbox-col-assignee">
                        <select
                          className="my-checklists-filter-select"
                          value={checklistAssignedScFilter}
                          onChange={(e) => setChecklistAssignedScFilter(e.target.value)}
                          aria-label="Filter by Assigned SC"
                        >
                          <option value="">All</option>
                          {staffList.map((s) => (
                            <option key={s.id} value={s.id}>
                              {[s.first_name, s.last_name].filter(Boolean).join(' ') || `Staff ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </th>
                    )}
                    <th className="inbox-col-type">
                      <select
                        className="my-checklists-filter-select"
                        value={checklistTypeFilter}
                        onChange={(e) => setChecklistTypeFilter(e.target.value)}
                        aria-label="Filter by Type"
                      >
                        <option value="">All</option>
                        {checklistTypes.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.type_name || t.type_code || '—'}
                          </option>
                        ))}
                      </select>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checklistListLoading && checklistList.length === 0 ? (
                    <tr>
                      <td colSpan={viewingAllStaffChecklists ? 3 : 2} className="no-data">Loading…</td>
                    </tr>
                  ) : displayedChecklistList.length === 0 ? (
                    <tr>
                      <td colSpan={viewingAllStaffChecklists ? 3 : 2} className="no-data">
                        {checklistList.length === 0 ? 'No active checklists' : 'No checklists match the filters.'}
                      </td>
                    </tr>
                  ) : (
                    displayedChecklistList.map((row) => {
                      const clientName = [row.client_last_name, row.client_first_name].filter(Boolean).join(', ') || '—';
                      const typeLabel = row.type_name || row.type_code || '—';
                      return (
                        <tr key={row.id} className="inbox-row">
                          <td className="inbox-col-client">
                            <Link to={tenantPath(`/client/${row.client_id}`)} className="notifications-view-link">
                              {clientName}
                            </Link>
                            {isCoverageDashboardRow(row, 'assignee_id') && (
                              <span className="dashboard-coverage-badge" title="You are covering for this client's primary SC">Cov</span>
                            )}
                          </td>
                          {viewingAllStaffChecklists && (
                            <td className="inbox-col-assignee">
                              {row.assignee_first_name || row.assignee_last_name
                                ? [row.assignee_first_name, row.assignee_last_name].filter(Boolean).join(' ')
                                : '—'}
                            </td>
                          )}
                          <td className="inbox-col-type">
                            <Link
                              to={tenantPath(`/referral/${row.referral_id}/checklists`)}
                              className="notifications-view-link"
                              state={{ clientId: row.client_id, checklistId: row.id }}
                            >
                              {typeLabel}
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {expandedPanel === 'worktasks' && (
          <section className="assignments-section worktasks-open-section">
            <div className="inbox-section-header">
              <h2 className="assignments-heading">
                My To-Dos (open work tasks &amp; your general items)
                {!scOwnCaseloadOnly && staffFilter && staffFilter !== myStaffId
                  ? (() => {
                      const s = staffList.find((x) => String(x.id) === staffFilter);
                      return s ? ` for ${s.first_name} ${s.last_name}` : '';
                    })()
                  : viewingAllStaffChecklists ? ' (all staff)' : ''}
              </h2>
            </div>
            <div className="inbox-box">
              <table className="inbox-table worktasks-open-table">
                <thead>
                  <tr className="table-header-row">
                    <th
                      className="inbox-col-client worktasks-open-sortable"
                      onClick={() => toggleWorkTasksOpenSort('client')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleWorkTasksOpenSort('client'); } }}
                      title="Sort by client last name"
                    >
                      Client{workTasksOpenSort.by === 'client' ? (workTasksOpenSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th
                      className="inbox-col-type worktasks-open-sortable"
                      onClick={() => toggleWorkTasksOpenSort('item')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleWorkTasksOpenSort('item'); } }}
                      title="Sort by item (General vs Work task)"
                    >
                      Item{workTasksOpenSort.by === 'item' ? (workTasksOpenSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                    <th className="inbox-col-type">Detail</th>
                    <th
                      className="worktasks-open-sortable"
                      onClick={() => toggleWorkTasksOpenSort('date')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleWorkTasksOpenSort('date'); } }}
                      title="Sort by due date (general) or requested date (work task)"
                    >
                      Due / requested{workTasksOpenSort.by === 'date' ? (workTasksOpenSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {workTasksOpenListLoading && workTasksOpenList.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="no-data">Loading…</td>
                    </tr>
                  ) : workTasksOpenList.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="no-data">No open items in this view (open work tasks not yet received, general reminders on your clients, or personal to-dos in range).</td>
                    </tr>
                  ) : (
                    displayedWorkTasksOpenList.map((row) => {
                      const clientName = [row.client_last_name, row.client_first_name].filter(Boolean).join(', ') || '—';
                      const isGeneral = row.kind === 'general';
                      const dateLabel = isGeneral
                        ? (row.due_date ? formatDisplayDate(row.due_date) : '—')
                        : (row.requested_date ? formatDisplayDate(row.requested_date) : '—');
                      const itemLabel = isGeneral ? 'General' : 'Work task';
                      const detailLabel = isGeneral
                        ? (row.title || '—')
                        : `${row.work_type_name || '—'} – ${row.task_type_name || '—'}`;
                      /** Client / referral to-dos (general or work-task): open My To-Dos on that client. */
                      const clientScopedTodoUrl =
                        row.referral_id && row.client_id
                          ? `${tenantPath(`/client/${row.client_id}/work-tasks`)}?referralId=${encodeURIComponent(row.referral_id)}&taskId=${encodeURIComponent(row.id)}`
                          : null;
                      /** Left-nav personal items only (no client / referral). */
                      const personalTodoUrl =
                        isGeneral && !row.referral_id
                          ? `${tenantPath('/lists/my-todos')}?taskId=${encodeURIComponent(row.id)}`
                          : null;
                      const rowOpenUrl = clientScopedTodoUrl || personalTodoUrl;
                      const goRow = rowOpenUrl ? () => navigate(rowOpenUrl) : undefined;
                      const isRowClickable = Boolean(goRow);
                      return (
                        <tr
                          key={row.id}
                          className={isRowClickable ? 'inbox-row clickable-row' : 'inbox-row'}
                          onClick={goRow}
                          role={isRowClickable ? 'button' : undefined}
                          tabIndex={isRowClickable ? 0 : undefined}
                          onKeyDown={isRowClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goRow(); } } : undefined}
                        >
                          <td className="inbox-col-client">
                            {row.client_id ? (
                              <>
                                <Link to={tenantPath(`/client/${row.client_id}`)} className="notifications-view-link" onClick={(e) => e.stopPropagation()}>
                                  {clientName}
                                </Link>
                                {isCoverageDashboardRow(row) && (
                                  <span className="dashboard-coverage-badge" title="You are covering for this client's primary SC">Cov</span>
                                )}
                                {row.owner_user_id && myUserId && String(row.owner_user_id) !== myUserId && (
                                  <span className="todo-added-by-badge" title={`Added by ${[row.creator_first_name, row.creator_last_name].filter(Boolean).join(' ') || 'another user'}`}>
                                    Added by {[row.creator_first_name, row.creator_last_name].filter(Boolean).join(' ') || 'someone'}
                                  </span>
                                )}
                              </>
                            ) : personalTodoUrl ? (
                              <Link
                                to={personalTodoUrl}
                                className="notifications-view-link"
                                onClick={(e) => e.stopPropagation()}
                                title="Personal to-do (not tied to a client)"
                              >
                                General
                              </Link>
                            ) : (
                              <span>General</span>
                            )}
                          </td>
                          <td className="inbox-col-type">{itemLabel}</td>
                          <td>{detailLabel}</td>
                          <td>
                            {rowOpenUrl ? (
                              <Link to={rowOpenUrl} className="notifications-view-link" onClick={(e) => e.stopPropagation()}>
                                {dateLabel}
                              </Link>
                            ) : (
                              dateLabel
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!expandedPanel && (
          <p className="dashboard-detail-empty">Click a card above to see tracking, notifications, checklists, or My To-Dos.</p>
        )}
      </div>

    </div>
  );
}

export default WorkTasksDashboard;
